import { collection, doc, addDoc, getDocs, query, where, orderBy, onSnapshot, serverTimestamp, limit as firestoreLimit, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

// ═══════════════ CONTENT FILTER PATTERNS ═══════════════
// Detects attempts to share contact info, off-platform payment, or alternative comms.
// Each pattern is a [regex, label] tuple. Patterns are case-insensitive.
const FILTER_PATTERNS = [
  // Phone numbers — Australian (04xx, +614xx, 02 xxxx) and generic 10+ digit sequences
  [/(\+?\d[\d\s-]{8,})/, 'phone_number'],
  // Email addresses
  [/[\w.+-]+@[\w-]+\.[\w.-]+/i, 'email'],
  // Off-platform comms apps
  [/\b(whats?app|telegram|signal|viber|wechat|messenger|snap(chat)?|insta(gram)?)\b/i, 'alt_comms'],
  // Off-platform payment talk
  [/\b(cash|venmo|paypal|zelle|cashapp|cash\s?app|beem|payid|bank\s?transfer|direct\s?deposit)\b/i, 'off_platform_payment'],
  // Direct contact / circumvention phrases
  [/\b(call\s+me|text\s+me|message\s+me|contact\s+me|outside\s+(the\s+)?app|off\s+(the\s+)?app|directly|skip\s+(the\s+)?app)\b/i, 'circumvention'],
];

// Returns { blocked: bool, label: string|null }
export function scanMessageContent(text) {
  if (!text) return { blocked: false, label: null };
  for (const [regex, label] of FILTER_PATTERNS) {
    if (regex.test(text)) {
      return { blocked: true, label };
    }
  }
  return { blocked: false, label: null };
}

// ═══════════════ LOG FLAGGED MESSAGE (admin audit) ═══════════════
async function logFlaggedMessage(bookingId, userId, userRole, text, label) {
  try {
    await addDoc(collection(db, 'flaggedMessages'), {
      bookingId,
      userId,
      userRole,
      text,
      label,
      timestamp: serverTimestamp(),
    });
  } catch (err) {
    // Non-fatal — don't block UX even if log fails
    console.error('Failed to log flagged message:', err);
  }
}

// ═══════════════ SEND MESSAGE ═══════════════
// Returns { sent: true } on success, { blocked: true, label } if content filter triggered.
export async function sendMessage(bookingId, senderId, senderRole, text) {
  const trimmed = text.trim();
  if (!trimmed) throw new Error('Message is empty');

  // Client-side content filter (defence in depth — Firestore rules also enforce participant + status)
  const scan = scanMessageContent(trimmed);
  if (scan.blocked) {
    // Log flagged attempt to admin audit collection
    await logFlaggedMessage(bookingId, senderId, senderRole, trimmed, scan.label);
    return { sent: false, blocked: true, label: scan.label };
  }

  await addDoc(collection(db, 'bookings', bookingId, 'messages'), {
    senderId,
    senderRole, // 'rider' | 'driver'
    text: trimmed,
    readByRecipient: false,
    createdAt: serverTimestamp(),
  });
  return { sent: true };
}

// ═══════════════ GET MESSAGES ═══════════════
export async function getMessages(bookingId) {
  const q = query(collection(db, 'bookings', bookingId, 'messages'), orderBy('createdAt', 'asc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// ═══════════════ SUBSCRIBE TO MESSAGES ═══════════════
export function subscribeToMessages(bookingId, callback) {
  const q = query(collection(db, 'bookings', bookingId, 'messages'), orderBy('createdAt', 'asc'));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
}

// ═══════════════ MARK AS READ ═══════════════
export async function markMessagesRead(bookingId, userId) {
  const q = query(
    collection(db, 'bookings', bookingId, 'messages'),
    where('readByRecipient', '==', false)
  );
  const snap = await getDocs(q);
  const batch = [];
  snap.docs.forEach((d) => {
    if (d.data().senderId !== userId) {
      batch.push(d.ref);
    }
  });
  const { writeBatch } = await import('firebase/firestore');
  const b = writeBatch(db);
  batch.forEach((ref) => b.update(ref, { readByRecipient: true }));
  await b.commit();
}